import { ColumnResizedEvent, GridApi, GridReadyEvent } from '@ag-grid-community/core';
import { DestroyRef, Directive, ElementRef, inject, input } from '@angular/core';
import { takeUntilDestroyed } from '@angular/core/rxjs-interop';
import { Subject } from 'rxjs';
import { debounceTime, filter } from 'rxjs/operators';

interface ColumnWidthState {
  [columnId: string]: number;
}

@Directive({
  host: {
    '(columnResized)': 'onColumnResized($event)',
    '(gridReady)': 'onGridReady($event)',
  },
  selector: '[auxGridColumnWidthPersistence]',
  standalone: true,
})
export class GridColumnWidthPersistenceDirective {
  destroyRef = inject(DestroyRef);
  elementRef = inject(ElementRef);

  gridId = input.required<string>({
    alias: 'auxGridColumnWidthPersistence',
  });

  columnResizedSubject = new Subject<ColumnResizedEvent>();
  DEBOUNCE_TIME = 300; // ms
  gridApi: GridApi | null = null;
  STORAGE_KEY_PREFIX = 'ag-grid-column-widths-';

  applySavedColumnWidths(): void {
    if (!this.gridApi) return;

    try {
      const savedWidths = localStorage.getItem(this.getStorageKey());

      if (!savedWidths) return;

      const columnWidths: ColumnWidthState = JSON.parse(savedWidths);

      this.applyWidthsToColumns(columnWidths);
    } catch (error) {
      console.warn('Failed to apply saved column widths:', error);
    }
  }

  applyWidthsToColumns(columnWidths: ColumnWidthState): void {
    if (!this.gridApi) return;

    const columns = this.gridApi.getAllDisplayedColumns();
    const columnState = columns
      .map((column) => {
        const columnId = column.getColId();
        const savedWidth = columnWidths[columnId];

        return {
          colId: columnId,
          width: savedWidth && savedWidth > 0 ? savedWidth : undefined,
        };
      })
      .filter((state) => state.width !== undefined);

    if (columnState.length > 0) {
      this.gridApi.applyColumnState({
        defaultState: { width: undefined },
        state: columnState,
      });
    }
  }

  clearSavedColumnWidths(): void {
    try {
      localStorage.removeItem(this.getStorageKey());
    } catch (error) {
      console.warn('Failed to clear saved column widths:', error);
    }
  }

  constructor() {
    this.columnResizedSubject
      .pipe(
        filter((event) => event.finished),
        debounceTime(this.DEBOUNCE_TIME),
        takeUntilDestroyed()
      )
      .subscribe(() => {
        this.saveColumnWidths();
      });
  }

  getStorageKey(): string {
    return `${this.STORAGE_KEY_PREFIX}${this.gridId()}`;
  }

  onColumnResized(event: ColumnResizedEvent) {
    this.columnResizedSubject.next(event);
  }

  onGridReady(event: GridReadyEvent) {
    this.gridApi = event.api;

    // // Apply saved widths after a short delay to ensure columns are ready
    setTimeout(() => {
      this.applySavedColumnWidths();
    }, 0);
  }

  saveColumnWidths(): void {
    if (!this.gridApi) return;

    const columnWidths: ColumnWidthState = {};
    const columns = this.gridApi.getAllDisplayedColumns();

    columns.forEach((column) => {
      columnWidths[column.getColId()] = column.getActualWidth();
    });

    try {
      localStorage.setItem(this.getStorageKey(), JSON.stringify(columnWidths));
    } catch (error) {
      console.warn('Failed to save column widths to localStorage:', error);
    }
  }
}
